import React, { useState } from "react";
import { getLeagueConfig } from "../../utils/leagueConfig";
import Button, { PrimaryButton, SecondaryButton } from "../../central/Button";
import { useToast } from "../../context/ToastContext";
import { computeMatchResult, RESULT_COLORS } from "./MatchScoreStep";
import { OWN_GOAL_ID } from "../../utils/matchConstants";

const ReviewSubmitStep = ({
  // data
  matchScore = {},
  appearanceData = {},
  starters = {},
  goals = [],
  cleanSheet = false,
  infractions = [],                // array of selected infraction names (optional)
  infractionAssignments = {},      // { [infractionName]: playerId | playerId[] }
  bestPlayers = {},                // { 1: pid, 2: pid, 3: pid }
  players = [],
  leagueType = "7-a-side",

  // actions
  onBack,
  onSubmit,
  isSubmitting = false,
  bonusWarning = "",
}) => {
  const [showBonusConfirm, setShowBonusConfirm] = useState(false);
  const { showToast } = useToast();

  const leagueConfig = getLeagueConfig(leagueType);
  const bonusSlots = leagueConfig.bonusSlots;

  // ---------- helpers ----------
  const getPlayerName = (id) => {
    if (!id) return "None";
    const p = players.find((x) => x.id === id);
    return p ? p.name : id; // fallback to id if somehow not found
  };

  const getPlayerPosition = (id) => {
    if (!id) return "";
    const p = players.find((x) => x.id === id);
    return p && p.position ? ` (${p.position})` : "";
  };

  const appearedIds = Object.keys(appearanceData).filter((id) => {
    const ap = appearanceData?.[id];
    return ap && (ap.appeared === true || ap.appeared === "true");
  });

  const starterIds = Array.isArray(starters)
    ? starters
    : Object.keys(starters || {}).filter((id) => !!starters[id]);

  // ---------- debug ----------
  console.log("🟢 ReviewSubmitStep — props:", {
    appearanceData,
    starters,
    goals,
    cleanSheet,
    infractions,
    infractionAssignments,
    bestPlayers,
    playersCount: players.length,
    leagueType,
    bonusSlots,
  });
  console.log("✅ Appeared IDs:", appearedIds);
  console.log("✅ Starter IDs:", starterIds);

  // ---------- local validation before final submit ----------
  const validateBeforeSubmit = () => {
    // If there are appeared players, skip errors about no appearances or starter appearing mismatch
    if (appearedIds.length > 0) {
      // infractions selected ⇒ must be assigned to appearing players or players confirmed appeared in appearanceData
      const entries = Object.entries(infractionAssignments || {});
      for (const [name, val] of entries) {
        const list = Array.isArray(val) ? val : val ? [val] : [];
        if (list.length === 0) {
          showToast(`Infraction "${name}" was selected but not assigned.`, "error");
          return false;
        }
        const bad = list.filter(
          (pid) =>
            !appearedIds.includes(pid) &&
            !(appearanceData[pid]?.appeared === true || appearanceData[pid]?.appeared === "true")
        );
        if (bad.length > 0) {
          showToast(
            `Infraction "${name}" assigned to invalid player(s): ${bad
              .map(getPlayerName)
              .join(", ")}`,
            "error"
          );
          return false;
        }
      }

      return true;
    }

    // If no players appeared, show errors about starters and appearances
    // starters ⊆ appeared
    const invalidStarters = starterIds.filter((id) => !appearedIds.includes(id));
    if (invalidStarters.length > 0) {
      console.warn("Starter check failed", { starterIds, appearedIds, appearanceData });
      showToast(
        `One or more starters were not marked as appeared: ${invalidStarters
          .map(getPlayerName)
          .join(", ")}`,
        "error"
      );
      return false;
    }

    showToast("At least one player must be marked as appeared.", "error");
    return false;
  };

  const handleSubmit = () => {
    if (isSubmitting) return;

    if (!validateBeforeSubmit()) return;

    if (Object.keys(bestPlayers || {}).length === 0) {
      setShowBonusConfirm(true);
      return;
    }

    try {
      console.log("📤 Submitting (final check dump):", {
        appearanceData,
        starters,
        goals,
        cleanSheet,
        infractions,
        infractionAssignments,
        bestPlayers,
      });
      onSubmit();
    } catch (err) {
      console.error("❌ Submit error:", err);
      showToast("Failed to submit match data. Please try again.", "error");
    }
  };

  const confirmBonusSubmit = () => {
    setShowBonusConfirm(false);
    onSubmit();
  };

  const cancelBonusSubmit = () => {
    setShowBonusConfirm(false);
  };

  // ---------- match score (optional) ----------
  const opponentTrimmed = typeof matchScore?.opponent === "string" ? matchScore.opponent.trim() : "";
  const hasYourScore = matchScore?.yourScore !== "" && matchScore?.yourScore !== undefined && matchScore?.yourScore !== null;
  const hasTheirScore = matchScore?.theirScore !== "" && matchScore?.theirScore !== undefined && matchScore?.theirScore !== null;
  const hasMatchScoreData = opponentTrimmed !== "" || hasYourScore || hasTheirScore;
  const matchResult = computeMatchResult(matchScore?.yourScore, matchScore?.theirScore);
  const opponentLabel = opponentTrimmed !== "" ? opponentTrimmed : "Opponent";

  // ---------- styles ----------
  const sectionStyle = {
    marginBottom: 8,
  };

  const itemStyle = {
    margin: "1px 0",
    lineHeight: "1.35",
  };

  const dividerStyle = {
    height: 1,
    backgroundColor: "#e6e6e6",
    margin: "10px 0 14px",
  };

  // ---------- render ----------
  return (
    <div>
      <h2>Review & Submit</h2>

      {/* Match Score (optional — skipped entirely if nothing was filled in) */}
      {hasMatchScoreData && (
        <>
          <h3>Match Score</h3>
          <div style={sectionStyle}>
            <p style={itemStyle}>
              vs {opponentLabel}
              {hasYourScore && hasTheirScore ? `: ${matchScore.yourScore}–${matchScore.theirScore}` : ""}
              {matchResult && (
                <span
                  style={{
                    display: "inline-block",
                    marginLeft: 8,
                    padding: "1px 9px",
                    borderRadius: "999px",
                    background: `${RESULT_COLORS[matchResult]}1A`,
                    color: RESULT_COLORS[matchResult],
                    fontWeight: 700,
                    fontSize: "0.8em",
                  }}
                >
                  {matchResult}
                </span>
              )}
            </p>
          </div>
          <div style={dividerStyle} />
        </>
      )}

      {/* Appearances */}
      <h3>Matchday Squad</h3>
      <div style={sectionStyle}>
        {Object.entries(appearanceData).map(([id, data]) => (
          <p key={id} style={itemStyle}>
            {getPlayerName(id)}
            {data.isGK ? " 🧤" : ""}
          </p>
        ))}
      </div>
      <div style={dividerStyle} />

      {/* Starters */}
      <h3>Starters</h3>
      <div style={sectionStyle}>
        {starterIds.map((id) => (
          <p key={id} style={itemStyle}>{getPlayerName(id)}</p>
        ))}
      </div>
      <div style={dividerStyle} />

      {/* Goals */}
      <h3>Goals</h3>
      <div style={sectionStyle}>
        {goals.length > 0 ? (
          goals.map((g, i) => (
            <p key={i} style={itemStyle}>
              {g.scorer === OWN_GOAL_ID ? "Own Goal" : getPlayerName(g.scorer)} (Assist: {g.assist ? getPlayerName(g.assist) : "None"})
            </p>
          ))
        ) : (
          <p style={itemStyle}>None</p>
        )}
      </div>
      <div style={dividerStyle} />

      {/* Clean sheet */}
      <h3>Clean Sheet</h3>
      <p style={{ marginBottom: 8, lineHeight: "1.35" }}>{cleanSheet === true ? "✅ Yes" : cleanSheet === false ? "❌ No" : "—"}</p>
      <div style={dividerStyle} />

      {/* Infractions (filtered to only selected with valid assignments) */}
      <h3>Infractions</h3>
      <div style={sectionStyle}>
        {infractionAssignments && Object.keys(infractionAssignments).length > 0 ? (
          Object.entries(infractionAssignments)
            .filter(([name, val]) => infractions.includes(name) && val && (Array.isArray(val) ? val.length > 0 : true))
            .map(([name, val]) => {
              const list = Array.isArray(val) ? val : val ? [val] : [];
              const validPlayers = list.map(getPlayerName).filter(Boolean);
              if (validPlayers.length === 0) return null;
              return (
                <p key={name} style={itemStyle}>
                  {name}: {validPlayers.join(", ")}
                </p>
              );
            })
        ) : infractions && infractions.length > 0 ? (
          <p style={itemStyle}>
            {infractions.join(", ")} (no players assigned yet)
          </p>
        ) : (
          <p style={itemStyle}>None</p>
        )}
      </div>
      <div style={dividerStyle} />

      {/* Bonus players */}
      {bonusWarning ? (
        <div
          style={{
            background: "#fff7db",
            color: "#6b5b00",
            border: "1px solid #f0dd8a",
            borderRadius: 10,
            padding: "10px 12px",
            marginBottom: 12,
            lineHeight: "1.4",
            fontSize: "0.95rem",
            maxWidth: "520px",
          }}
        >
          {bonusWarning}
        </div>
      ) : null}
      <h3>Bonus Players</h3>
      <div style={sectionStyle}>
        {bonusSlots > 0 ? (
          Array.from({ length: bonusSlots }, (_, i) => {
            const slot = (i + 1).toString(); // force string key
            const suffix =
              slot === "1" ? "st" :
              slot === "2" ? "nd" :
              slot === "3" ? "rd" : "th";
            const playerId = bestPlayers[slot];
            const playerName = playerId ? getPlayerName(playerId) : "None";
            const playerPosition = playerId ? getPlayerPosition(playerId) : "";
            console.log(`🟢 Bonus slot ${slot}: playerId=${playerId}, name=${playerName}, position=${playerPosition}`);
            return (
              <p key={slot} style={itemStyle}>
                {slot}{suffix} – {playerName}
              </p>
            );
          })
        ) : Object.keys(bestPlayers).length === 0 ? (
          <p style={itemStyle}>No bonus players selected.</p>
        ) : (
          Object.entries(bestPlayers).map(([slot, playerId]) => {
            const suffix =
              slot === "1" ? "st" :
              slot === "2" ? "nd" :
              slot === "3" ? "rd" : "th";
            const playerName = playerId ? getPlayerName(playerId) : "None";
            const playerPosition = playerId ? getPlayerPosition(playerId) : "";
            return (
              <p key={slot} style={itemStyle}>
                {slot}{suffix} – {playerName}
              </p>
            );
          })
        )}
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
        <SecondaryButton color="blue" onClick={onBack} disabled={isSubmitting}>
          Back
        </SecondaryButton>
        <PrimaryButton color="pink" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? "Submitting…" : "Submit Match"}
        </PrimaryButton>
      </div>

      {showBonusConfirm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "8px",
              maxWidth: "420px",
              textAlign: "center",
            }}
          >
            <h3 style={{ marginTop: 0 }}>No bonus points selected</h3>
            <p style={{ marginBottom: "1rem" }}>
              You haven't selected any bonus players. Submit anyway?
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
              <Button onClick={cancelBonusSubmit} disabled={isSubmitting}>
                Go Back
              </Button>
              <Button onClick={confirmBonusSubmit} variant="pink" disabled={isSubmitting}>
                Submit
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewSubmitStep;